/*!
=========================================================
* Regiform IMS Frontend - FilterDropdown Component
=========================================================
* Reusable dropdown component with search/filter functionality
* Built with Ant Design Select component
* Supports async data loading and real-time filtering
=========================================================
*/
import { useState, useMemo } from "react";
import { Select, Spin } from "antd";
import PropTypes from "prop-types";
import "./FilterDropdown.css";

const { Option } = Select;

const FilterDropdown = ({
  data = [],
  loading = false,
  placeholder = "Select an option",
  value,
  onChange,
  onSearch,
  searchValue,
  onSearchChange,
  disabled = false,
  allowClear = true,
  showSearch = true,
  filterOption = true,
  optionLabelProp = "label",
  fieldNames = { label: "name", value: "id" },
  className = "",
  style = {},
  size = "large",
  notFoundContent = null,
  dropdownRender,
  showViewLink = false,
  onViewClick,
  viewLinkText = "View",
  ...otherProps
}) => {
  const [internalSearchValue, setInternalSearchValue] = useState("");

  // Use external search value if provided, otherwise use internal
  const currentSearchValue =
    searchValue !== undefined ? searchValue : internalSearchValue;

  // Filter data based on search value
  const filteredData = useMemo(() => {
    if (!currentSearchValue || !filterOption) {
      return data;
    }

    return data.filter((item) => {
      const label = item[fieldNames.label] || "";
      return label.toLowerCase().includes(currentSearchValue.toLowerCase());
    });
  }, [data, currentSearchValue, filterOption, fieldNames.label]);

  // Handle search input change
  const handleSearch = (searchText) => {
    if (onSearchChange) {
      onSearchChange(searchText);
    } else {
      setInternalSearchValue(searchText);
    }

    // Call external search handler if provided
    if (onSearch) {
      onSearch(searchText);
    }
  };

  // Handle selection change
  const handleChange = (selectedValue, option) => {
    if (onChange) {
      onChange(selectedValue, option);
    }
  };

  // Handle clear
  const handleClear = () => {
    if (onChange) {
      onChange(undefined, null);
    }
    if (onSearchChange) {
      onSearchChange("");
    } else {
      setInternalSearchValue("");
    }
  };

  // Custom filter function for Ant Design Select
  const customFilterOption = (input, option) => {
    if (!filterOption) return true;

    const label = option?.children || option?.label || "";
    return label.toLowerCase().includes(input.toLowerCase());
  };

  // Custom not found content
  const getNotFoundContent = () => {
    if (loading) {
      return <Spin size="small" />;
    }
    if (notFoundContent !== null) {
      return notFoundContent;
    }
    return currentSearchValue
      ? `No results found for "${currentSearchValue}"`
      : "No data available";
  };

  // Find selected option for view link
  const selectedOption = useMemo(() => {
    return data.find((item) => item[fieldNames.value] === value);
  }, [data, value, fieldNames.value]);

  // Handle view link click
  const handleViewClick = (e) => {
    e.stopPropagation();
    if (onViewClick && selectedOption) {
      onViewClick(selectedOption);
    }
  };

  return (
    <div className={`filter-dropdown-container ${className}`} style={style}>
      <div className="filter-dropdown-wrapper">
        <Select
          className={`filter-dropdown ${className}`}
          style={{ flex: 1 }}
          size={size}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onSearch={showSearch ? handleSearch : undefined}
          onClear={handleClear}
          loading={loading}
          disabled={disabled}
          allowClear={allowClear}
          showSearch={showSearch}
          filterOption={showSearch ? customFilterOption : false}
          optionLabelProp={optionLabelProp}
          notFoundContent={getNotFoundContent()}
          dropdownRender={dropdownRender}
          searchValue={showSearch ? currentSearchValue : undefined}
          {...otherProps}
        >
          {filteredData.map((item) => (
            <Option
              key={item[fieldNames.value]}
              value={item[fieldNames.value]}
              label={item[fieldNames.label]}
            >
              {item[fieldNames.label]}
            </Option>
          ))}
        </Select>

        {/* View Link */}
        {showViewLink && selectedOption && (
          <button
            type="button"
            className="filter-dropdown-view-link"
            onClick={handleViewClick}
            title={`${viewLinkText} ${selectedOption[fieldNames.label]}`}
          >
            {viewLinkText}
          </button>
        )}
      </div>
    </div>
  );
};

FilterDropdown.propTypes = {
  // Data and loading
  data: PropTypes.array,
  loading: PropTypes.bool,

  // Basic props
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool,

  // Search functionality
  showSearch: PropTypes.bool,
  onSearch: PropTypes.func,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  filterOption: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),

  // Display options
  optionLabelProp: PropTypes.string,
  fieldNames: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),

  // Styling
  className: PropTypes.string,
  style: PropTypes.object,
  size: PropTypes.oneOf(["small", "middle", "large"]),

  // Custom content
  notFoundContent: PropTypes.node,
  dropdownRender: PropTypes.func,

  // View link functionality
  showViewLink: PropTypes.bool,
  onViewClick: PropTypes.func,
  viewLinkText: PropTypes.string,
};

export default FilterDropdown;
